import React, {FC, useCallback, useRef, useState, useEffect} from "react";
import {
  Container,
  TitleContainer,
  TitleWrapper,
  InfoText,
  LinkWrapper,
  Link,
} from './styles'
import { ServiceActivationBadge, ServiceActivationContent, ServiceActivationState } from '../../../components/service-activation/styles'
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { Button, ModalVariables, Table, TableSkeleton, TopProgress, Typography } from "../../../components";
import { objStorageService } from "../../../api";
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../utils/display-price";
import Skeleton from 'react-loading-skeleton'
import { useTheme } from "styled-components";
import { handleStatusName } from "../../../utils/handle-status-name";
import { useNavigate } from "react-router";
import LinkIc from './link.svg'
import { icons } from "../../../icons";
import { useUi } from "../../../context/ui.context";
import { formatFileSizeRounded, formatFileSize } from "../../../utils/sizes";
import { permissionSurfaces, usePermission, usePermissionDecision } from '../../../permissions';
import {
  isObjectStorageServiceInactiveError,
} from "../../../utils/object-storage-service";
const BucketsPage:FC = () => {
  const theme:any = useTheme()
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam)
  const [paginationData, setPaginationData] = useState<any>()
  const [invoices, setInvoices] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [selectedBucket, setSelectedBucket] = useState<any>(null)
  const [serviceActivationRequired, setServiceActivationRequired] = useState<boolean>(false)
  const [serviceOrderLoading, setServiceOrderLoading] = useState<boolean>(false)

  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>()
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const navigate = useNavigate()
  const {viewAlert} = useUi()
  const [t] = useTranslation()
  const { mergeEntityPermissions } = usePermission()
  const canCreateBucket = usePermissionDecision(permissionSurfaces.pageActions.objectStorageBucketCreate).allowed
  const bucketRequestId = useRef(0)
  const getInvoices = useCallback(async (): Promise<boolean> => {
    const requestId = ++bucketRequestId.current
    setLoading(true)
    try {
      const res = await objStorageService.getBuckets({
        page:page || 1,
        page_size:pageSize || 10,
        ordering:ordering || '',
      })

      if (requestId !== bucketRequestId.current) {
        return false
      }

      mergeEntityPermissions('objectStorage', res.data, 'Merged bucket list permissions')
      setInvoices(res.data.objects.map((invoice:any) => {
        return {
          ...invoice,
          issue_date: timeConverterMMDDYYHHMM(invoice.issue_date),
          navigate:invoice.id.toString(),
          // status:<StatusText status={invoice.status}>{handleStatusName(invoice.status, t)}</StatusText>,
          total:formatCurrency(invoice.total, invoice.currency),
          usage:formatFileSize(invoice.usage)
        }
      }))
      setPaginationData(res.data)
      setServiceActivationRequired(false)
      return true
    } catch (error) {
      if (requestId !== bucketRequestId.current) {
        return false
      }

      if (isObjectStorageServiceInactiveError(error)) {
        setInvoices([])
        setPaginationData(undefined)
        setServiceActivationRequired(true)
      } else {
        setServiceOrderLoading(false)
        if ((error as any)?.response?.status === 403) {
          navigate('/403', { replace: true })
        }
        console.log('error', error);
      }

      return false
    } finally {
      if (requestId === bucketRequestId.current) {
        setPageLoading(false)
        setLoading(false)
      }
    }
  }, [mergeEntityPermissions, navigate, ordering, page, pageSize])

  useEffect(() => {
    setPageLoading(true)
    setServiceActivationRequired(false)
    setServiceOrderLoading(false)
    void getInvoices()
    return () => {
      bucketRequestId.current += 1
    }
  }, [getInvoices])

  useEffect(() => {
    if (serviceActivationRequired && !serviceOrderLoading) {
      return
    }

    const interval = setInterval(() => {
      void getInvoices()
    }, 5000)

    return () => {
      clearInterval(interval)
    }
  }, [getInvoices, serviceActivationRequired, serviceOrderLoading])

  useEffect(() => {
    if (!serviceOrderLoading || serviceActivationRequired) {
      return
    }

    setServiceOrderLoading(false)
    viewAlert({
      severity: 'success',
      message: t("OBJECT_STORAGE_ACTIVATED"),
    })
  }, [serviceActivationRequired, serviceOrderLoading, t, viewAlert])

  const createServiceOrder = async () => {
    setServiceOrderLoading(true)

    try {
      await objStorageService.createServiceOrder()
      await getInvoices()
    } catch (error:any) {
      setServiceOrderLoading(false)
      viewAlert({
        severity: 'error',
        message: error?.response?.data?.detail || t("ERROR_OCCURRED"),
      })
    }
  }
  const ColumnsInvoices = [
    {
      title:'Name',
      key:'name'
    },
    {
      title:t("VISIBILITY"),
      key:'access',
    },
    // {
    //   title:t("POLICY"),
    //   key:'policies1'
    // },
    {
      title:t("OBJECTS"),
      key:'num_objects'
    },
    {
      title:t("SIZE"),
      key:'usage'
    },
    {
      title:'',
      key:'props'
    }
  ]
  const props = [
    {
      title:t("BUCKET_SETTINGS"),
      id:'1',
      icon:icons.Edit,
      permissionSurface: permissionSurfaces.rowActions.bucketSettings,
      onClick: (bucket:any) => {
        navigate(`${bucket.id}/settings`)
      },
      divider:false
    },
    {
      title:t("DELETE"),
      id:'7',
      permissionSurface: permissionSurfaces.rowActions.bucketDelete,
      onClick:(bucket:any) => {
        openModal('delete')
        setAlertTitle(t("BUCKET"))
        setAlertSubTitle([bucket])
        setSelectedBucket(bucket)
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]
  const openModal = (modal:any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }
  const closeModalCancel = () => {
    setViewModal(false)
    setModalType('')
    setActiveAnimation(false)
  }
  const deleteBucket = async () => {
    setLoading(true);
    try {
      if (selectedBucket) {
        await objStorageService.deleteBucket(selectedBucket.id);
        // _updateImages();
      }
    } catch (e:any) {
      if (isObjectStorageServiceInactiveError(e)) {
        setInvoices([])
        setPaginationData(undefined)
        setServiceActivationRequired(true)
      } else if (e?.response?.data.detail) {
        viewAlert({
          severity: 'error',
          message: e.response.data.detail,
        });
      }
      // setChecked([]);
    } finally {
      setViewModal(false);
      setActiveAnimation(false);
      setLoading(false);
    }
  }
  const closeModal = async (props?:any) => {
    let res:any;
    try {
      setLoading(true);
      switch (modalType) {
        case 'add-folder':
          //@ts-ignore
          res = await objStorageService.addFolder(bucketId, {name:`${path}${props?.name}`})
          if (res?.data?.detail) {
            viewAlert({
              severity: 'info',
              message: res.data?.detail,
            });
          }
          break;
        // case 'upload-volume-to-image':
        //   res = await volumeService.uploadToImage(selectedVolume.id, values);
        //   if (res?.data?.detail) {
        //     viewAlert({
        //       severity: 'info',
        //       message: res.data?.detail,
        //     });
        //   }
        //   break;
        case 'delete':
          deleteBucket()
          break; 
      }
      setLoadingComplete(true)
      setTimeout(() => {
        setLoading(false)
        setLoadingComplete(false)
        setViewModal(false)
        setActiveAnimation(false)
        setModalType('')
      },1000)
      if(modalType !== 'delete'){
        setViewModal(false)
        setActiveAnimation(false)
      }
    } catch (e: any) {
      console.log('e',e);
      if (e?.response?.data.detail) {
        viewAlert({
          severity: 'error',
          message: e.response.data.detail,
        });
      }
    } finally {
      if(modalType !== 'delete'){
        setLoading(false);
      }
    }
  }

  if(serviceActivationRequired) return (
    <Container>
      <TopProgress loading={serviceOrderLoading}/>
      <ServiceActivationState>
        <ServiceActivationBadge>S3</ServiceActivationBadge>
        <ServiceActivationContent>
          <Typography variant="h2">
            {t("OBJECT_STORAGE_SERVICE_REQUIRED")}
          </Typography>
          <Typography variant="body">
            {t("OBJECT_STORAGE_SERVICE_REQUIRED_DESCRIPTION")}
          </Typography>
          <Button
            variant="primary"
            size="display"
            loading={serviceOrderLoading}
            onClick={createServiceOrder}
            title={t("ACTIVATE_OBJECT_STORAGE")}
          />
        </ServiceActivationContent>
      </ServiceActivationState>
    </Container>
  )

  if(pageLoading) return (
    <Container>
      <TopProgress loading={pageLoading}/>
      <Skeleton
        baseColor={theme.palette[300]}
        highlightColor={theme.palette[20]}
        width={125} style={{marginBottom:"5px"}} height={30}
      />
      <TableSkeleton
        rows={10}
        columns={5}
        // columnsData={Columns}
      />
    </Container>
  )
  return (
    <Container>
      <TitleContainer>
        <TitleWrapper>
          <Typography variant='h1'>
            {t("BUCKETS_TITLE")}
          </Typography>
          <InfoText>
            {t("BUCKETS_SUBTITLE")}
          </InfoText>
          <LinkWrapper>
            <Link>{t("BUCKETS_LINK")}</Link>    
            <img src={LinkIc} alt='LinkIc'/>      
          </LinkWrapper>
        </TitleWrapper>
        <Button
          variant="primary"
          size="display"
          customStyles={{whiteSpace:'nowrap'}}
          disabled={!canCreateBucket}
          onClick={() => navigate('/object-storage/create')}
          title={`+ ${t("CREATE")}`}
          // loading={loading}
          // loadingComplete={loadingComplete}
        />
      </TitleContainer>
      {invoices?.length > 0 ?
        <Table
          columns={ColumnsInvoices}
          rows={invoices}
          // hideOther
          hideCheckbox
          settings={props}
          paginationData={paginationData}
        />
      :
        <Typography variant="body">
          {t("NO_BUCKETS")}
        </Typography>
      }
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        instance={selectedBucket}
        // loading={loading}
        // loadingComplete={loadingComplete}
        alertSubTitle={alertSubTitle}
      />
    </Container>
  )
}

export default BucketsPage
